import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { trpc } from '@/config/trpc'
// import { GradientAnimation } from '@/components/ui/GradientAnimation' // wash paused — see below
import { PlumeField } from '@/components/ui/PlumeField'
import { burstEnergy } from '@/lib/energyBus'
import { PromptBox } from '@/components/ui/prompt-box'
import { useSidebarStore } from '@/stores/sidebarStore'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu'
import { ChevronDown, PlusIcon } from 'lucide-react'
import { useTheme } from '@/context/themeContext'
import { cn, uploadToS3 } from '@/lib/utils'
import { captureError } from '@/lib/errors'
import { HomeTopicSuggestions } from '@/components/home/home-topic-suggestions'
import { BrainstormView } from '@/components/home/brainstorm-view'
import {
  type GenerationStyle,
  type DesignAspectRatioChoice,
  GenerationStylePopover,
} from '@/components/home/generation-style-popover'
import { McpStatusHint } from '@/components/developer/mcp-status-hint'
import { UpgradeModal } from '@/components/upgrade-modal'
import { useBrand } from '@/context/brandContext'
import { pendingGenerationStore } from '@/stores/pendingGenerationStore'
import { buildStarterTemplate } from '@/lib/onboarding/starterTemplate'
import { HomeMoodboardNotice } from '@/components/home/home-moodboard-notice'
import { pollWhileAnalyzing, useMoodboardGate } from '@/components/home/use-moodboard-gate'

export function HomePage() {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const { isLoading: isBrandLoading, isSwitching, currentBrand } = useBrand()
  const [openMoodboardDropdown, setOpenMoodboardDropdown] = useState(false)

  const { closeSidebar } = useSidebarStore()

  const [userPrompt, setUserPrompt] = useState('')
  const [userFiles, setUserFiles] = useState<File[]>([])
  const [userUrls, setUserUrls] = useState<string[]>([])
  const [showBrainstorm, setShowBrainstorm] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [upgradeLimitType, setUpgradeLimitType] = useState('')
  // Every knob starts on Auto (absent): the brand brief decides unless the user
  // picks something for this post.
  const [generationStyle, setGenerationStyle] = useState<GenerationStyle>({})
  // 'auto' keeps the generator's per-topic ratio choice; an explicit value
  // overrides every generated design (single, options, and carousel).
  const [designAspectRatio, setDesignAspectRatio] = useState<DesignAspectRatioChoice>('auto')

  // trpc
  const utils = trpc.useUtils()

  const { data: moodboards, isLoading: isMoodboardsLoading } = trpc.moodboard.getAll.useQuery(
    undefined,
    { refetchInterval: pollWhileAnalyzing },
  )
  const { mutateAsync: createMoodboard, isPending: isCreatingMoodboard } =
    trpc.moodboard.create.useMutation()

  const { mutateAsync: createWorkspace } = trpc.workspace.create.useMutation()
  const { mutateAsync: getUploadUrl } = trpc.asset.getUploadUrl.useMutation()

  const { mutate: setLastUsedMoodboard } = trpc.brand.setLastUsedMoodboard.useMutation({
    onSuccess: () => utils.brand.getCurrent.invalidate(),
  })

  const isResolvingSelection = isBrandLoading || isMoodboardsLoading || isSwitching

  const [selectedMoodboardId, setSelectedMoodboardId] = useState<number | null>(null)
  const sortedMoodboards = useMemo(() => {
    if (!moodboards) return []
    return [...moodboards].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    )
  }, [moodboards])
  const selectedMoodboard = useMemo(() => {
    if (selectedMoodboardId === null) return null
    return moodboards?.find((p) => p.id === selectedMoodboardId) ?? null
  }, [selectedMoodboardId, moodboards])

  // Auto-select the brand's last-used moodboard (persisted server-side, so it
  // survives sessions and devices), falling back to the first ready board so
  // generation has a brand source instead of generic. A dangling or not-ready
  // lastUsedMoodboardId (deleted board, items changed) falls through to the
  // fallback. A user's explicit pick sticks.
  useEffect(() => {
    if (isResolvingSelection) return
    if (selectedMoodboardId !== null) return
    const lastUsed = sortedMoodboards.find(
      (m) => m.id === currentBrand?.lastUsedMoodboardId && m.status === 'ready',
    )
    const fallback = sortedMoodboards.find((m) => m.status === 'ready')
    const initial = lastUsed ?? fallback
    if (initial) setSelectedMoodboardId(initial.id)
  }, [isResolvingSelection, selectedMoodboardId, sortedMoodboards, currentBrand])

  const { gate: moodboardGate, blocksSubmit } = useMoodboardGate({
    moodboards,
    selectedMoodboard,
    isResolving: isResolvingSelection,
  })

  // handlers

  // Fallback for a brand that reaches home without a moodboard (skipped or
  // left onboarding, or deleted its only board): same starter board as
  // onboarding, opened in the regular canvas.
  const handleCreateMoodboard = async () => {
    try {
      const name = currentBrand?.name ? `${currentBrand.name} moodboard` : 'My moodboard'
      const result = await createMoodboard({ name, items: buildStarterTemplate() })
      if (!result.ok) {
        setUpgradeLimitType('moodboards')
        setShowUpgradeModal(true)
        return
      }
      void utils.moodboard.getAll.invalidate()
      navigate(`/moodboards/${result.moodboard.id}`)
    } catch (error) {
      captureError(error, { flow: 'home.createMoodboard' })
      toast.error('Could not create the moodboard. Please try again.')
    }
  }

  const handleSelectMoodboard = (moodboardId: number) => {
    setSelectedMoodboardId(moodboardId)
    setLastUsedMoodboard({ moodboardId })
  }

  // The home page no longer generates anything itself: it creates the
  // workspace, uploads any attached files, and parks the request in the
  // pending-generation store. The workspace chat (the CASSI agent) picks it up
  // as its first message and streams the captions straight into the editor —
  // so the workspace is created 'success', not behind the full-screen
  // 'generating' state.
  const handlePostSubmit = async () => {
    if (!userPrompt.trim() && userFiles.length === 0 && userUrls.length === 0) return
    // The moodboard is read from this render. Submitting before it resolves
    // (or while its brief is still generating) sends a post with no brand.
    if (blocksSubmit) return

    // Pre-flight check: show upgrade modal before navigating away
    // fetch() returns cached data if available, otherwise makes a real request
    const usageData = await utils.usageLimit.get.fetch()
    if (!usageData.post.canUse) {
      setUpgradeLimitType('post')
      setShowUpgradeModal(true)
      return
    }

    let workspaceId: number | undefined = undefined
    let step: 'create-workspace' | 'upload' = 'create-workspace'

    try {
      const tempWorkspaceId = 'temp-' + Date.now()

      navigate(`/workspace/${tempWorkspaceId}`)
      closeSidebar()

      const newWorkspace = await createWorkspace({
        title: userPrompt,
        status: 'success',
        moodboardId: selectedMoodboard?.id ?? null,
      })
      workspaceId = newWorkspace.id

      pendingGenerationStore.getState().setPending({
        workspaceId,
        prompt: userPrompt,
        contextURLs: userUrls,
        files: [],
        moodboardId: selectedMoodboard?.id ?? null,
        style: generationStyle,
        designAspectRatio,
        status: userFiles.length > 0 ? 'uploading' : 'ready',
      })

      navigate(`/workspace/${workspaceId}`)
      closeSidebar()

      if (userFiles.length > 0) {
        step = 'upload'
        const uploadedUserFiles = await Promise.all(
          userFiles.map(async (file) => {
            let subStep: 'sign' | 'put' = 'sign'
            try {
              const { key, uploadUrl, assetUrl } = await getUploadUrl({
                type: 'workspace',
                workspaceId: workspaceId!,
              })
              subStep = 'put'
              await uploadToS3(uploadUrl, file)
              return { key, assetUrl, file }
            } catch (error) {
              const cause = error instanceof Error ? error.message : String(error)
              throw new Error(
                `Upload failed (${subStep}, ${file.name}, ${file.size}b, ${file.type || 'unknown'}): ${cause}`,
                { cause: error },
              )
            }
          }),
        )

        pendingGenerationStore.getState().updatePending(workspaceId, {
          files: uploadedUserFiles.map(({ key, assetUrl, file }) => ({
            key,
            url: assetUrl,
            name: file.name,
            type: file.type,
            size: file.size,
          })),
          status: 'ready',
        })
      }
    } catch (error) {
      captureError(error, {
        flow: 'home.generatePost',
        step,
        workspaceId,
        fileCount: userFiles.length,
        totalFileBytes: userFiles.reduce((sum, f) => sum + f.size, 0),
        urlCount: userUrls.length,
        hasMoodboard: selectedMoodboard != null,
      })

      if (workspaceId != null) {
        pendingGenerationStore.getState().clearPending(workspaceId)
      }

      const errorMessage = error instanceof Error ? error.message : String(error)
      const cleanErrorMessage = errorMessage.replace(/^TRPCClientError:\s*/i, '')
      toast.error('Error generating post', {
        description: cleanErrorMessage,
        duration: 7000,
      })
      navigate('/')
    }
  }

  const startingBlankRef = useRef(false)

  /** Create an empty workspace and go straight to it. Deliberately skips the
   *  pending-generation handoff: nothing to generate, so no agent run, no
   *  caption written for you, and no images added to a workspace you meant to
   *  fill yourself. Also skips the `temp-` placeholder route, which only exists
   *  to cover the wait for `createWorkspace`. */
  const handleStartBlank = async () => {
    // Creating a workspace is a round trip; a second click while it is in
    // flight would leave an orphan behind.
    if (startingBlankRef.current) return
    startingBlankRef.current = true
    try {
      const newWorkspace = await createWorkspace({
        title: 'Untitled post',
        status: 'success',
        moodboardId: selectedMoodboard?.id ?? null,
      })
      closeSidebar()
      navigate(`/workspace/${newWorkspace.id}`)
    } catch (error) {
      captureError(error, { flow: 'home.startBlank', hasMoodboard: selectedMoodboard != null })
      toast.error('Could not create the workspace', {
        description: error instanceof Error ? error.message : String(error),
      })
    } finally {
      startingBlankRef.current = false
    }
  }

  const handleFileDelete = (index: number) => {
    console.log('Deleting file at index:', index)
    setUserFiles((prev) => {
      const newFiles = prev.filter((_, i) => i !== index)
      return newFiles
    })
  }

  const handleBrainstormFinalized = (topic: string, refinedPrompt: string) => {
    setShowBrainstorm(false)
    setUserPrompt(refinedPrompt || topic)
    // A whole refined prompt just landed in the box — the field reacts in kind.
    burstEnergy()
  }

  // If in brainstorm mode, show the full-page brainstorm view
  if (showBrainstorm) {
    return (
      <BrainstormView
        moodboard={selectedMoodboard}
        onBack={() => setShowBrainstorm(false)}
        onTopicFinalized={handleBrainstormFinalized}
      />
    )
  }

  return (
    <div className="flex h-full min-h-[calc(100dvh-3rem)] flex-col p-4 md:min-h-screen">
      {/* background wash paused while we evaluate the plume field alone */}
      {/* <GradientAnimation animationDuration="25s" /> */}
      {/* full-width plume field hugging the viewport bottom — low presence so it
          starts small and grows into the old idle height at full energy; big
          slow plumes for the big canvas */}
      <PlumeField
        className="fixed inset-x-0 bottom-[-4vh] h-[32vh]"
        tempo={0.45}
        driftScale={2.2}
        presence={0.15}
        plumeScale={1.5}
      />

      <div
        className={cn(
          'relative mx-auto mt-[15vh] w-full max-w-[690px] transition-opacity duration-200',
          isSwitching && 'pointer-events-none opacity-20',
        )}>
        <div className="animate-fade-down animate-once animate-duration-500 animate-delay-100 animate-ease-in">
          <h1 className="text-dark-gray-900 mx-auto mb-3 w-full max-w-[700px] text-center text-2xl font-semibold lg:text-3xl">
            What do you want to talk about today?
          </h1>
          <p className="text-muted-foreground mx-auto mb-8 w-full max-w-[700px] text-center text-sm lg:text-base">
            A product update, a tip, a story, or an idea - CASSI will shape it.
          </p>
        </div>

        <div className="animate-fade-down animate-once animate-duration-500 animate-delay-300 animate-ease-in">
          {(isResolvingSelection || sortedMoodboards.length > 0) && (
            <div className="absolute -top-[29px] right-4 flex items-center gap-2">
              {isResolvingSelection ? (
                <div className="border-border bg-card flex h-7 min-w-[132px] animate-pulse items-center rounded-full border px-3">
                  <div className="bg-muted h-3 w-20 rounded-full" />
                </div>
              ) : (
                <DropdownMenu open={openMoodboardDropdown} onOpenChange={setOpenMoodboardDropdown}>
                  <DropdownMenuTrigger asChild>
                    <button className="border-border bg-card hover:bg-hover text-foreground flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs transition-colors">
                      <span className="max-w-[160px] truncate">
                        {selectedMoodboard?.name ?? 'Select moodboard'}
                      </span>
                      <ChevronDown className="size-4 shrink-0" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className={cn('w-[220px]', theme === 'dark' ? 'dark' : '')}
                    side="bottom"
                    align="end"
                    sideOffset={8}>
                    <DropdownMenuLabel className="text-muted-foreground text-[10px] uppercase tracking-wide">
                      Moodboards
                    </DropdownMenuLabel>
                    {sortedMoodboards.map((moodboard) => {
                      // Only a board with a generated brief can drive generation.
                      // `analyzing` boards (brief baking in the background, e.g.
                      // straight out of onboarding) show their state and stay
                      // disabled until ready.
                      const ready = moodboard.status === 'ready'
                      const statusLabel =
                        moodboard.status === 'analyzing'
                          ? 'Analyzing…'
                          : moodboard.status === 'failed'
                            ? 'Failed'
                            : moodboard.status === 'draft'
                              ? 'Not analyzed'
                              : null
                      return (
                        <DropdownMenuItem
                          key={`moodboard-${moodboard.id}`}
                          disabled={!ready}
                          onClick={() => {
                            if (!ready) return
                            handleSelectMoodboard(moodboard.id)
                            setOpenMoodboardDropdown(false)
                          }}
                          className="flex cursor-pointer items-center justify-between gap-2 p-2">
                          <span className="truncate text-xs font-medium">{moodboard.name}</span>
                          {statusLabel && (
                            <span className="text-muted-foreground text-[9px] uppercase">
                              {statusLabel}
                            </span>
                          )}
                        </DropdownMenuItem>
                      )
                    })}
                    <DropdownMenuItem
                      onClick={() => {
                        navigate('/moodboards')
                        setOpenMoodboardDropdown(false)
                      }}
                      className="flex cursor-pointer items-center gap-2 p-2">
                      <span className="truncate text-xs font-medium">
                        <span className="flex items-center gap-2">
                          <PlusIcon width={12} height={12} />
                          New Moodboard
                        </span>
                      </span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}

          <PromptBox
            submitDisabled={blocksSubmit}
            value={userPrompt}
            onChange={setUserPrompt}
            onSubmit={handlePostSubmit}
            files={userFiles}
            onFileChange={(file) => setUserFiles(file)}
            onFileDelete={handleFileDelete}
            urls={userUrls}
            onUrlAdd={(url) => setUserUrls((prev) => [...prev, url])}
            onUrlUpdate={(index, url) =>
              setUserUrls((prev) => prev.map((u, i) => (i === index ? url : u)))
            }
            onUrlDelete={(index) => setUserUrls((prev) => prev.filter((_, i) => i !== index))}
            onBrainstormClick={() => setShowBrainstorm(true)}
            onStartBlank={handleStartBlank}
            settingsSlot={
              <GenerationStylePopover
                style={generationStyle}
                onChange={setGenerationStyle}
                aspectRatio={designAspectRatio}
                onAspectRatioChange={setDesignAspectRatio}
              />
            }
          />

          {/* Below the prompt box: the moodboard pill above it belongs to the box. */}
          <HomeMoodboardNotice
            gate={moodboardGate}
            onOpenMoodboard={(id) => navigate(`/moodboards/${id}`)}
            onCreateMoodboard={() => void handleCreateMoodboard()}
            isCreating={isCreatingMoodboard}
          />

          <div className="relative left-1/2 mt-6 w-[780px] max-w-[92vw] -translate-x-1/2">
            <HomeTopicSuggestions
              onTopicSelect={(topic) => {
                setUserPrompt(topic)
                // Selecting a suggestion drops a full prompt into the box at
                // once — burst the plume field instead of a keystroke tick.
                burstEnergy()
              }}
              selectedMoodboard={selectedMoodboard}
              isSelectionLoading={isResolvingSelection}
            />
          </div>

          {/* The no-moodboard nudge lives in HomeMoodboardNotice above. */}
          {moodboards && moodboards.length > 0 && <McpStatusHint />}
        </div>
      </div>

      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        limitType={upgradeLimitType}
        surface="home"
      />
    </div>
  )
}
